import React, { useState, useEffect, useCallback } from 'react';
import { STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from './CommercialDashboard';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
import { fmtDate } from '../../utils/dateUtils';

const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
const fmt = (d) => fmtDate(d);

const TYPE_LABELS = {
  general_contractor: 'General Contractor', custom_home_builder: 'Custom Home Builder',
  commercial_builder: 'Commercial Builder', developer: 'Developer', property_investor: 'Property Investor',
  site_contractor: 'Site Contractor', civil_contractor: 'Civil Contractor', ranch_developer: 'Ranch Developer',
  equestrian_business: 'Equestrian', property_manager: 'Property Mgmt', equipment_yard: 'Equipment Yard',
  industrial: 'Industrial', utility_contractor: 'Utility Contractor', government: 'Government', other: 'Other',
};

export default function CommercialAccounts({ navigate }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filterType) params.set('account_type', filterType);
    if (filterPriority) params.set('account_priority', filterPriority);
    if (filterStage) params.set('account_status', filterStage);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts?${params}`, authOpts());
      if (res.ok) setAccounts(await res.json());
    } finally { setLoading(false); }
  }, [search, filterType, filterPriority, filterStage]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="commercial-accounts-view" style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 16, flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>Commercial Accounts</h1>
          <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>{accounts.length} account{accounts.length !== 1 ? 's' : ''}</p>
        </div>
        <button data-testid="create-account-btn" onClick={() => setShowCreate(true)}
          style={{ padding: '8px 20px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 40 }}>
          + New Account
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input data-testid="account-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search company, email, phone..."
          style={{ flex: 1, minWidth: isMobile ? '100%' : 200, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
        <select data-testid="filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, flex: isMobile ? '1 1 45%' : undefined }}>
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="filter-priority" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, flex: isMobile ? '1 1 45%' : undefined }}>
          <option value="">All Priority</option>
          <option value="A">A — Dream</option>
          <option value="B">B — Strong</option>
          <option value="C">C — Prospect</option>
        </select>
        <select data-testid="filter-stage" value={filterStage} onChange={e => setFilterStage(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, flex: isMobile ? '1 1 45%' : undefined }}>
          <option value="">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Account List */}
      {isMobile ? (
        /* Mobile: Card Layout */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading...</div> :
            accounts.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No accounts found.</div> :
            accounts.map(acct => {
              const stageColor = STAGE_COLORS[acct.account_status] || '#94a3b8';
              const priColor = PRIORITY_COLORS[acct.account_priority] || '#94a3b8';
              return (
                <div key={acct.id} data-testid={`account-row-${acct.id}`} onClick={() => navigate(`/sales/commercial/account/${acct.id}`)}
                  style={{ background: '#fff', borderRadius: 10, padding: '14px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', cursor: 'pointer', borderLeft: `4px solid ${priColor}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acct.company_name}</div>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${stageColor}15`, color: stageColor, flexShrink: 0 }}>
                      {STAGE_LABELS[acct.account_status] || acct.account_status}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#64748b', flexWrap: 'wrap' }}>
                    <span>{TYPE_LABELS[acct.account_type] || acct.account_type}</span>
                    {acct.city && <span>{acct.city}{acct.state ? `, ${acct.state}` : ''}</span>}
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{money(acct.open_pipeline)}</span>
                    {acct.lifetime_won > 0 && <span style={{ color: '#16a34a', fontWeight: 600 }}>Won: {money(acct.lifetime_won)}</span>}
                  </div>
                </div>
              );
            })
          }
        </div>
      ) : (
        /* Desktop: Table Layout */
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Company</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, color: '#475569', width: 40 }}>P</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Type</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Stage</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Pipeline</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Won</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Contacts</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Last Contact</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No accounts found. Click "New Account" to start building your commercial pipeline.</td></tr>
            ) : accounts.map(acct => {
              const stageColor = STAGE_COLORS[acct.account_status] || '#94a3b8';
              const priColor = PRIORITY_COLORS[acct.account_priority] || '#94a3b8';
              return (
                <tr key={acct.id} data-testid={`account-row-${acct.id}`}
                  onClick={() => navigate(`/sales/commercial/account/${acct.id}`)}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background .1s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{acct.company_name}</div>
                    {acct.city && <div style={{ fontSize: 10, color: '#94a3b8' }}>{acct.city}{acct.state ? `, ${acct.state}` : ''}</div>}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: priColor, color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {acct.account_priority}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 11, color: '#64748b' }}>{TYPE_LABELS[acct.account_type] || acct.account_type}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${stageColor}15`, color: stageColor, border: `1px solid ${stageColor}30` }}>
                      {STAGE_LABELS[acct.account_status] || acct.account_status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#1e293b' }}>{money(acct.open_pipeline)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>{money(acct.lifetime_won)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: '#475569' }}>{acct.contacts_count || 0}</td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{fmt(acct.last_contact_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreate && <CreateAccountModal
        onClose={() => { setShowCreate(false); setDuplicates([]); }}
        onCreated={(id) => { setShowCreate(false); setDuplicates([]); load(); if (id) navigate(`/sales/commercial/account/${id}`); }}
        duplicates={duplicates}
        setDuplicates={setDuplicates}
        navigate={navigate}
      />}
    </div>
  );
}


function CreateAccountModal({ onClose, onCreated, duplicates, setDuplicates, navigate }) {
  const [form, setForm] = useState({
    company_name: '', account_type: 'general_contractor', account_priority: 'C',
    main_phone: '', main_email: '', website: '', physical_address: '', city: '', state: 'TX', zip: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const checkDupes = async () => {
    if (!form.company_name.trim()) return;
    setChecking(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/check-duplicate`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ company_name: form.company_name, phone: form.main_phone, email: form.main_email, website: form.website }),
      });
      if (res.ok) {
        const data = await res.json();
        setDuplicates(data.duplicates || []);
      }
    } finally { setChecking(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts`, {
        method: 'POST', headers: h(), body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated(data.id);
      }
    } finally { setSaving(false); }
  };

  const fieldStyle = { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div data-testid="create-account-modal" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', padding: '20px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: 0 }}>New Commercial Account</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={labelStyle}>Company Name *</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input data-testid="new-company-name" value={form.company_name} onChange={e => set('company_name', e.target.value)}
                onBlur={checkDupes} style={{ ...fieldStyle, flex: 1 }} placeholder="e.g. ABC Construction LLC" />
              {checking && <span style={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>Checking...</span>}
            </div>
          </div>

          {/* Duplicate Warning */}
          {duplicates.length > 0 && (
            <div data-testid="duplicate-warning" style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>Possible Duplicate Accounts</div>
              {duplicates.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: i < duplicates.length - 1 ? '1px solid #fecaca' : 'none' }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{d.company_name}</span>
                    <span style={{ marginLeft: 6, fontSize: 10, color: '#dc2626', fontWeight: 700 }}>{d.confidence}% match</span>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{d.reasons?.join(' | ')}</div>
                  </div>
                  <button onClick={() => { onClose(); navigate(`/sales/commercial/account/${d.account_id}`); }}
                    style={{ padding: '3px 8px', fontSize: 10, fontWeight: 600, background: '#fff', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 4, cursor: 'pointer' }}>
                    View
                  </button>
                </div>
              ))}
              <div style={{ fontSize: 10, color: '#dc2626', marginTop: 6 }}>You can still create the account if these are different companies.</div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle}>Account Type</label>
              <select data-testid="new-account-type" value={form.account_type} onChange={e => set('account_type', e.target.value)} style={fieldStyle}>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Priority</label>
              <select data-testid="new-priority" value={form.account_priority} onChange={e => set('account_priority', e.target.value)} style={fieldStyle}>
                <option value="A">A — Dream / High-Value Target</option>
                <option value="B">B — Strong Potential</option>
                <option value="C">C — Prospect / Unproven</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={labelStyle}>Phone</label><input data-testid="new-phone" value={form.main_phone} onChange={e => set('main_phone', e.target.value)} style={fieldStyle} /></div>
            <div><label style={labelStyle}>Email</label><input data-testid="new-email" value={form.main_email} onChange={e => set('main_email', e.target.value)} style={fieldStyle} /></div>
          </div>
          <div><label style={labelStyle}>Website</label><input data-testid="new-website" value={form.website} onChange={e => set('website', e.target.value)} style={fieldStyle} placeholder="example.com" /></div>
          <div><label style={labelStyle}>Physical Address</label><input value={form.physical_address} onChange={e => set('physical_address', e.target.value)} style={fieldStyle} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <div><label style={labelStyle}>City</label><input value={form.city} onChange={e => set('city', e.target.value)} style={fieldStyle} /></div>
            <div><label style={labelStyle}>State</label><input value={form.state} onChange={e => set('state', e.target.value)} style={fieldStyle} /></div>
            <div><label style={labelStyle}>Zip</label><input value={form.zip} onChange={e => set('zip', e.target.value)} style={fieldStyle} /></div>
          </div>
          <div><label style={labelStyle}>Notes</label><textarea data-testid="new-notes" value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...fieldStyle, resize: 'vertical' }} /></div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#fff', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="save-account-btn" onClick={save} disabled={!form.company_name.trim() || saving}
            style={{ padding: '8px 24px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, background: form.company_name.trim() ? '#c0392b' : '#94a3b8', color: '#fff', cursor: form.company_name.trim() ? 'pointer' : 'default' }}>
            {saving ? 'Creating...' : duplicates.length > 0 ? 'Create Anyway' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
